import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import { ConfigContext } from '../../contexts/config-context';
import { UserContext } from '../../contexts/userContext';
import ListingAPI from '../../axios/listing';
import { formatNumber, LISTING_ROUTES, PRICE_UNITS } from '../main-page/home-utils';
import { MessageModal } from '../search-page/components/message-modal';
import { PhoneIcon, SpecIcon } from '../../components/shared-components/listing-icons';
import { AmenityIcon } from './components/amenity-icons';
import { findForm, floorLabel, numberLabel, plotAreaLabel, useListingForms } from '../../components/shared-components/listing-forms';
import { ContactPanel } from './components/contact-panel';
import { Gallery } from './components/gallery';
import { LoanCalculator } from './components/loan-calculator';
import { LocationSection } from './components/location-section';
import { ReportModal } from './components/report-modal';
import { ShareMenu } from './components/share-menu';
import { SimilarListings } from './components/similar-listings';
import "../../hs-theme.css";
import "./listing-detail.css";

// descriptions longer than this start collapsed
const LONG_DESCRIPTION = 420;
// listing flags shown as rows in the characteristics instead of chips;
// an urgent sale already has its badge above the title
const SPEC_FLAGS = ['registered', 'vat', 'mortgage', 'habitable', 'urgent'];
// the checkbox groups of sales and rentals, shown together as amenities; a short-term rental shows its groups apart
const AMENITY_GROUPS = ['additional', 'other'];
// the prices of a short-term rental for the longer stays
const STAY_PRICES = ['price_4_7', 'price_8_15', 'price_16_31', 'price_new_year'];
// the sticky site header, which the price bar sits under
const HEADER_HEIGHT = 73;

export const ListingDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation(["details", "listing", "home"]);
    const { searchData } = useContext(ConfigContext);
    const { user } = useContext(UserContext);
    const lang = i18n.language;
    const { forms } = useListingForms(lang);

    const [listing, setListing] = useState(null);
    const [liked, setLiked] = useState(false);
    // users who saved the listing; kept apart so saving it here updates the number at once
    const [followers, setFollowers] = useState(0);
    const [descOpen, setDescOpen] = useState(false);
    const [messageOpen, setMessageOpen] = useState(false);
    const [reportOpen, setReportOpen] = useState(false);
    // phones and tablets: the price under the gallery, and whether it has scrolled up out of view
    const [priceEl, setPriceEl] = useState(null);
    const [priceBar, setPriceBar] = useState(false);

    // once the price has scrolled up under the header, a bar with it stays at the top
    useEffect(() => {
        if (!priceEl) return;
        const observer = new IntersectionObserver(([entry]) => {
            setPriceBar(!entry.isIntersecting && entry.boundingClientRect.top < HEADER_HEIGHT);
        }, { rootMargin: `-${HEADER_HEIGHT}px 0px 0px 0px` });
        observer.observe(priceEl);
        return () => observer.disconnect();
    }, [priceEl]);

    useEffect(() => {
        let cancelled = false;
        setListing(null);
        setDescOpen(false);
        window.scrollTo(0, 0);
        ListingAPI.getListingDetails(id, lang)
            .then(res => {
                if (cancelled) return;
                setListing(res.data);
                setFollowers(res.data.followers || 0);
            })
            .catch(err => {
                console.log(err);
                // the server answers a missing listing with 500 rather than 404
                if (!cancelled && err.response) navigate('/nepostojeci-oglas');
            });
        return () => { cancelled = true; };
    }, [id, lang]);

    useEffect(() => {
        setLiked(false);
        if (user) {
            ListingAPI.isListingLikedByUser(id).then(res => setLiked(res.data.is_liked)).catch(() => { });
        }
    }, [id, user]);

    if (!listing) return <div className='hs hs-detail'></div>;

    const typeId = listing.listing_type?.id || 1;
    const route = LISTING_ROUTES[typeId - 1];
    const placeSearch = { data: { ...searchData, listing_type: String(typeId), place: listing.place } };
    const price = parseFloat(listing.price) || 0;
    const area = parseFloat(listing.area) || 0;
    const bills = parseFloat(listing.bills) || 0;
    const fields = listing.fields || [];
    const groups = listing.features || [];
    const ticked = groups.flatMap(group => group.items.map(item => item.code));
    // the flags the listing's form offers, so "No" is only said of what could have been ticked
    const form = findForm(forms, typeId, listing.property_type?.id);
    const offeredFlags = new Set((form?.features || []).flatMap(group => group.items.map(item => item.code)));
    const rooms = numberLabel('num_rooms', listing.num_rooms);
    const baths = numberLabel('num_bathrooms', listing.num_bathrooms);
    const floor = floorLabel(listing, t);
    const plotArea = plotAreaLabel(listing);
    const yesNo = (flag) => offeredFlags.has(flag) && t(ticked.includes(flag) ? 'yes' : 'no');
    const optionLabels = (group) => (listing.options?.[group] || []).map(option => option.label).join(', ');
    const priceLabel = price > 0
        ? `${formatNumber(price)} €${typeId > 1 ? ` ${t(PRICE_UNITS[typeId - 1], { ns: 'home' })}` : ''}`
        : t('price_placeholder', { ns: 'listing' });
    const pricePerM2 = typeId === 1 && price > 0 && area > 0 ? `(${formatNumber(price / area)} €/m²)` : null;
    const description = (listing.description || '').trim();
    const longDescription = description.length > LONG_DESCRIPTION;

    const facts = [
        rooms && { icon: 'rooms', value: rooms, label: t('fact_rooms') },
        area > 0 && { icon: 'area', value: `${formatNumber(area)} m²`, label: t('fact_area') },
        !area && plotArea && { icon: 'area', value: plotArea, label: t('plot_area', { ns: 'listing' }) },
        baths && { icon: 'baths', value: baths, label: t('fact_baths') },
        floor && { icon: 'floor', value: floor, label: t('fact_floor') },
    ].filter(Boolean);

    // the picked values in the order the form asks for them, then the numbers it has
    const optionSpecs = fields.filter(name => listing.options?.[name]).map(name => [t(`group_${name}`, { ns: 'listing' }), optionLabels(name)]);
    const specs = [
        [t('type', { ns: 'listing' }), listing.property_type?.label],
        ...optionSpecs,
        [t('publisher', { ns: 'listing' }), listing.user_type?.label],
        [t('plot_area', { ns: 'listing' }), area > 0 && plotArea],
        [t('offices', { ns: 'listing' }), numberLabel('offices', listing.offices)],
        [t('phone_lines', { ns: 'listing' }), numberLabel('phone_lines', listing.phone_lines)],
        [t('guests', { ns: 'listing' }), numberLabel('guests', listing.guests)],
        [t('not_last_floor', { ns: 'listing' }), listing.not_last_floor && t('yes')],
        [t('habitable', { ns: 'listing' }), ticked.includes('habitable') && t('yes')],
        [t('registered', { ns: 'listing' }), yesNo('registered')],
        [t('vat', { ns: 'listing' }), yesNo('vat')],
        [t('mortgage', { ns: 'listing' }), yesNo('mortgage')],
        [t('bills', { ns: 'listing' }), bills > 0 && `${formatNumber(bills)} €`],
        ...STAY_PRICES.map(key => [t(key, { ns: 'listing' }), parseFloat(listing[key]) > 0 && `${formatNumber(listing[key])} €`]),
        [t('price_per_m2', { ns: 'listing' }), typeId === 1 && price > 0 && area > 0 && `${formatNumber(price / area)} €`],
    ].filter(([, value]) => value);

    // the amenities of a sale or rental together, a short-term rental's equipment by group
    const amenities = groups.filter(group => AMENITY_GROUPS.includes(group.group))
        .flatMap(group => group.items).filter(item => !SPEC_FLAGS.includes(item.code));
    const equipment = groups.filter(group => !AMENITY_GROUPS.includes(group.group));
    const chipList = (items) => (
        <div className='hs-det-chips'>
            {items.map(item => <span key={item.code}><AmenityIcon name={item.code} />{item.label}</span>)}
        </div>
    );

    const goBack = () => {
        // history.state.idx is 0 when the listing was opened directly
        if (window.history.state?.idx > 0) navigate(-1);
        else navigate(route);
    };

    const toggleLike = () => {
        if (!user) {
            navigate('/login');
            return;
        }
        const request = liked ? ListingAPI.unlikeListing(id) : ListingAPI.likeListing(id);
        request.then(() => {
            setLiked(!liked);
            setFollowers(count => Math.max(0, count + (liked ? -1 : 1)));
        }).catch(() => { });
    };

    // to the top of the contact card: price, publisher, call and message buttons and the viewing booking
    const goToContact = () => document.querySelector('.hs-contact')?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    const openMessage = () => {
        if (!user) {
            navigate('/login');
            return;
        }
        setMessageOpen(true);
    };

    // only logged-in users may report, so every report has a known sender
    const openReport = () => {
        if (!user) {
            navigate('/login');
            return;
        }
        setReportOpen(true);
    };

    // shown in the top row and again in the price bar
    const likeButton = (
        <button className={`hs-det-like ${liked ? 'on' : ''} ${followers > 0 ? 'has-count' : ''}`} onClick={toggleLike} aria-label={liked ? t('saved') : t('save')} aria-pressed={liked}
            data-tip={[liked ? t('unfollow_tip') : t('follow_tip'), followers > 0 && t('followers', { n: followers })].filter(Boolean).join('\n')}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill={liked ? '#FF5722' : 'none'} stroke={liked ? '#FF5722' : '#0E1A2B'} strokeWidth="2"><path d="M12 20.5s-7.5-4.6-9.3-9.3C1.4 7.8 3.6 4.5 7 4.5c2 0 3.6 1.1 5 3 1.4-1.9 3-3 5-3 3.4 0 5.6 3.3 4.3 6.7-1.8 4.7-9.3 9.3-9.3 9.3Z" /></svg>
            <span className='hs-det-tool-label'>{liked ? t('saved') : t('save')}</span>
            {followers > 0 && <span className='hs-det-like-count'>{followers}</span>}
        </button>
    );

    return (
        <div className='hs hs-detail'>
            <main className='hs-detail-main'>
                <div className='hs-det-top'>
                    <nav className='hs-det-crumbs'>
                        <button className='hs-det-back' onClick={goBack} aria-label={t('back')}>
                            <span className='hs-det-back-arrow' aria-hidden='true'>←</span>
                            <span className='hs-det-back-label'>{t('back')}</span>
                        </button>
                        <Link to='/'>{t('home')}</Link><span>/</span>
                        <Link to={route}>{listing.listing_type?.label}</Link><span>/</span>
                        <Link to={route} state={placeSearch}>{listing.place?.label}</Link>
                        {listing.subplace?.label && <><span>/</span><span className='current'>{listing.subplace.label}</span></>}
                    </nav>
                    <div className='hs-det-tools'>
                        <ShareMenu listing={listing} />
                        <button className='hs-det-report' onClick={openReport} aria-label={t('report_listing')} data-tip={t('report_listing')}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 21V4" /><path d="M5 4h11l-2 4 2 4H5" /></svg>
                            <span className='hs-det-tool-label'>{t('report_listing')}</span>
                        </button>
                        {likeButton}
                    </div>
                </div>

                <Gallery listing={listing} />

                <div className='hs-det-layout'>
                    <div className='hs-det-content'>
                        <section className='hs-det-section hs-det-basic'>
                            {/* the state and registration are in the characteristics; only an urgent sale is called out */}
                            {ticked.includes('urgent') &&
                                <div className='hs-det-badges'><span className='hot'>{t('urgent', { ns: 'listing' })}</span></div>}
                            {/* phones and tablets, where the contact panel with the price drops below the content */}
                            <div ref={setPriceEl} className='hs-det-mprice'>
                                <span className='hs-det-mprice-value'>{priceLabel}</span>
                                {pricePerM2 && <span className='hs-det-mprice-sub'>{pricePerM2}</span>}
                            </div>
                            <h1>{listing.title}</h1>
                            <p className='hs-det-loc'>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21Z" /><circle cx="12" cy="9.5" r="2.5" /></svg>
                                <span>
                                    {[listing.place?.label, listing.subplace?.label, listing.location?.label, listing.address].filter(Boolean).join(' · ')}
                                    <a href='#lokacija'>{t('show_on_map')}</a>
                                </span>
                            </p>
                            <div className='hs-det-facts'>
                                {facts.map(fact => (
                                    <div key={fact.icon} className='hs-det-fact'>
                                        <span className='hs-det-fact-icon'>
                                            <SpecIcon name={fact.icon} />
                                        </span>
                                        <span className='hs-det-fact-text'>
                                            <span className='hs-det-fact-value'>{fact.value}</span>
                                            <span className='hs-det-fact-label'>{fact.label}</span>
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </section>

                        {description &&
                            <section className='hs-det-section hs-det-desc-card'>
                                <h2>{t('description_title')}</h2>
                                <div className={`hs-det-desc ${longDescription && !descOpen ? 'collapsed' : ''}`}>
                                    {/* blank lines in the text split it into paragraphs */}
                                    {description.split(/\n\s*\n/).map((paragraph, i) => <p key={i}>{paragraph.trim()}</p>)}
                                </div>
                                {longDescription &&
                                    <button className='hs-det-desc-toggle' onClick={() => setDescOpen(!descOpen)}>
                                        {descOpen ? t('show_less') : t('show_more')}
                                    </button>}
                            </section>}

                        <section className='hs-det-section'>
                            <h2>{t('characteristics')}</h2>
                            <div className='hs-det-specs'>
                                {specs.map(([label, value]) => (
                                    <div key={label}><span>{label}</span><strong>{value}</strong></div>
                                ))}
                            </div>
                            {amenities.length > 0 &&
                                <div className='hs-det-amenities'>
                                    <h3>{t('amenities')}</h3>
                                    {chipList(amenities)}
                                </div>}
                            {equipment.map(group => (
                                <div key={group.group} className='hs-det-amenities'>
                                    <h3>{t(`feature_group_${group.group}`, { ns: 'listing' })}</h3>
                                    {chipList(group.items)}
                                </div>
                            ))}
                        </section>

                        <LocationSection listing={listing} />

                        {typeId === 1 && price > 0 && <LoanCalculator price={price} />}
                    </div>

                    <ContactPanel listing={listing} onMessage={openMessage} />
                </div>

                <SimilarListings listing={listing} />
            </main>

            <div className={`hs-det-pricebar ${priceBar ? 'on' : ''}`} aria-hidden={!priceBar}>
                <div className='hs-det-pricebar-text'>
                    <span className='hs-det-pricebar-price'>{priceLabel}</span>
                    {pricePerM2 && <span className='hs-det-pricebar-sub'>{pricePerM2}</span>}
                </div>
                <div className='hs-det-tools hs-det-pricebar-tools'>
                    <ShareMenu listing={listing} />
                    {likeButton}
                    <button onClick={goToContact} aria-label={t('contact')} title={t('contact')}>
                        <PhoneIcon size={18} strokeWidth={2} />
                    </button>
                </div>
            </div>

            {messageOpen && <MessageModal listing={listing} onClose={() => setMessageOpen(false)} />}
            {reportOpen && <ReportModal listing={listing} onClose={() => setReportOpen(false)} />}
        </div>
    );
};
